import { Component, OnInit, computed, inject, signal, ViewChild } from '@angular/core';
import { TableModule } from 'primeng/table';
import { GroupsTableComponent } from '../groups-table/groups-table.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
    AlertBannerComponent,
    MessageType,
    PasswordResetNotificationService,
    PasswordResetService,
    passwordResetErrorMessage,
    passwordResetErrorTitle
} from 'lib';
import { ToastrService } from 'ngx-toastr';
import { PasswordResetModalComponent } from '../password-reset/password-reset-modal/password-reset-modal.component';

@Component({
    selector: 'mmoga-groups-overview',
    standalone: true,
    imports: [TableModule, GroupsTableComponent, FormsModule, ReactiveFormsModule, CommonModule, RouterLink, AlertBannerComponent],
    templateUrl: './groups-overview.component.html',
    styleUrl: './groups-overview.component.scss'
})
export class GroupsOverviewComponent implements OnInit {
    private formBuilder = inject(FormBuilder);
    private modalService = inject(NgbModal);
    private passwordResetService = inject(PasswordResetService);
    private toastrService = inject(ToastrService);

    private readonly passwordResetNotificationService = inject(PasswordResetNotificationService);

    @ViewChild('groups') groups!: GroupsTableComponent;

    formGroupOverview: FormGroup;
    readonly isPreparingPasswordResetModal = signal(false);
    readonly isPasswordResetModalOpen = signal(false);
    readonly passwordResetNotification = this.passwordResetNotificationService.notification;
    readonly isPasswordResetProcessing = computed(() => {
        const notificationType = this.passwordResetNotification().type;
        return notificationType === MessageType.INFO || notificationType === MessageType.WARNING;
    });
    readonly isPasswordResetActionDisabled = computed(() =>
        this.isPreparingPasswordResetModal() || this.isPasswordResetModalOpen() || this.isPasswordResetProcessing()
    );
    readonly shouldShowPasswordResetBanner = computed(() =>
        this.passwordResetNotification().type !== null && !this.isPasswordResetModalOpen()
    );

    constructor() {
        this.formGroupOverview = this.formBuilder.group({
            searchGroupTerm: ['', []]
        });
    }

    get passwordResetBannerAlertType(): string {
        return this.passwordResetNotification().alertType ?? '';
    }

    get passwordResetBannerMessage(): string {
        return this.passwordResetNotification().message ?? '';
    }

    ngOnInit(): void {
        this.resetComponent();
    }

    searchGroup(): void {
        this.groups.searchTerm = this.formGroupOverview.controls['searchGroupTerm'].value;
        this.groups.selectedIds.clear();
        this.groups?.ngOnInit();
    }

    resetComponent(): void {
        this.formGroupOverview.controls['searchGroupTerm'].reset();
        if (this.groups) {
            this.groups.searchTerm = '';
            this.groups.selectedIds.clear();
            this.groups.ngOnInit();
        }
    }

    openPasswordResetModal(selectedIds: number[]): void {
        if (selectedIds.length === 0 || this.isPasswordResetActionDisabled()) {
            return;
        }

        this.isPreparingPasswordResetModal.set(true);
        this.passwordResetService.getActiveUserCountBatch(selectedIds).subscribe({
            next: totalUserCount => {
                const modalRef = this.modalService.open(PasswordResetModalComponent, { size: 'lg' });
                this.isPasswordResetModalOpen.set(true);
                modalRef.componentInstance.enterpriseIds = selectedIds;
                modalRef.componentInstance.totalUserCount = totalUserCount;
                void modalRef.result.finally(() => this.isPasswordResetModalOpen.set(false));
            },
            error: () => {
                this.isPreparingPasswordResetModal.set(false);
                this.toastrService.error(passwordResetErrorMessage, passwordResetErrorTitle);
            },
            complete: () => {
                this.isPreparingPasswordResetModal.set(false);
            }
        });
    }
}
